import React, { useEffect, useState } from "react";
import { cancelPayUPayment } from "../../../services/userService";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from '@iconify/react';

const CancelPayUPayment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const course_id = searchParams.get("course_id");
  const txnid = searchParams.get("txnid");

  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleCancelValidation = async () => {
    
    // console.log('=== PayU Cancel Payment Validation Started ===');
    // console.log('Course ID:', course_id);
    // console.log('Transaction ID:', txnid);

    try {
      // console.log('Calling cancelPayUPayment API...');
      const response = await cancelPayUPayment(course_id, txnid);
      // console.log("Cancel PayU payment response:", response);

      if (response.success) {
        // console.log('Payment cancellation processed:', response.data);
        setData(response.data);
      } else {
        console.error('Payment cancellation failed:', response);
        setHasError(true);
      }
    } catch (error) {
      console.error("Error in PayU cancel payment:", error);
      setHasError(true);
    } finally {
      // console.log('Setting loading to false');
      setIsLoading(false);
    }
  };

  function redirectToCourse(){
    navigate(`/user/courses`);
  }

  function retryPayment(){
    navigate(`/user/courses/orderDetailsWithPayu?course_id=${course_id}`);
  }

  useEffect(() => {
    // console.log('useEffect triggered with:', { course_id, txnid });
    if (course_id && txnid) {
      handleCancelValidation();
    } else {
      // console.log('Missing parameters, setting loading to false');
      setIsLoading(false);
      setHasError(true);
    }
  }, [course_id, txnid]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mb-3 mx-auto" role="status" />
          <p className="text-gray-500">Processing your PayU payment cancellation...</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <Icon icon="mdi:alert-circle" className="text-red-500 mb-3 mx-auto" width="64" height="64" />
          <h3 className="text-xl font-semibold mb-2">Oops! Something went wrong.</h3>
          <p className="text-gray-500 mb-4">We couldn't process your PayU payment cancellation. Please contact support.</p>
          <button className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg" onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-[60vh] p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 max-w-lg w-full">
        {/* Cancel Icon */}
        <div className="flex justify-center mb-4">
          <Icon icon="mdi:close-circle" className="text-yellow-500" width="64" height="64" />
        </div>

        {/* Cancel Message */}
        <h2 className="text-2xl font-semibold text-yellow-500 text-center mb-2">PayU Payment Cancelled</h2>
        <p className="text-gray-600 text-center mb-6">Your PayU payment transaction has been cancelled.</p>

        {/* Transaction Details */}
        <div className="space-y-3 mb-6">
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">Transaction ID</span>
            <span className="text-gray-900">
              #{txnid ? txnid.toString().toUpperCase().slice(0, 15) : "-"}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">Date</span>
            <span className="text-gray-900">
              {new Date().toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">Status</span>
            <span className="text-yellow-600 flex items-center gap-1">
              <Icon icon="mdi:close-circle" width="16" height="16" />
              Cancelled
            </span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="font-medium text-gray-700">Payment Method</span>
            <span className="text-gray-900">
              PayU Gateway
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mt-4">
          <button 
            className="bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2"
            onClick={retryPayment}
          >
            <Icon icon="mdi:refresh" width="20" height="20" />
            Try Payment Again
          </button>
          <button 
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2"
            onClick={redirectToCourse}
          >
            <Icon icon="mdi:arrow-left" width="20" height="20" />
            Back to Courses
          </button>
        </div>

        {/* Help Section */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <h6 className="font-semibold mb-2 flex items-center gap-2">
            <Icon icon="mdi:help-circle" width="20" height="20" />
            Need Help?
          </h6>
          <p className="text-sm text-gray-500">
            If you're experiencing issues with PayU payments, please contact our support team.
            No amount has been charged to your account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CancelPayUPayment;
